import React, { useContext, useEffect, useState } from 'react';
import { Grid, Icon, Box, LinearProgress } from '@mui/material';
import { useForm } from 'react-hook-form';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { useNavigate, useParams } from 'react-router-dom';
import {
  CustomPagination,
  summarizer,
  checkInfosProduto,
} from 'utils/functions';
import { toastWarning } from 'utils/toast';
import { AuthContext } from 'contexts/AuthContext';
import { DropsContext } from 'contexts/DropsContext';
import { DocumentosContext } from 'contexts/DocumentosContext';
import VirtualDrop from 'components/VirtualDrop';
import Container from 'components/Container';
import InputMask from 'components/InputMask';
import Header from 'components/Header';
import Button from 'components/Button';
import Card from 'components/Card';
import localeText from 'utils/localeText';
import moment from 'moment';

const Retorno = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const defaultValues = { produto_id: null, quantidade: '' };
  const { control, handleSubmit, reset } = useForm({ defaultValues });
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { documento, getLoading, postLoading, getDocumento, postDocumento } =
    useContext(DocumentosContext);
  const isLoading = loading || getLoading || postLoading;

  const itens_remessa = documento?.DocumentoItems || [];
  const casas_decimais_quantidade = 6;

  const normalizarDecimal = (value) => {
    const number = Number(value);

    if (isNaN(number)) {
      return 0;
    }

    const factor = 10 ** casas_decimais_quantidade;

    return Math.round((number + Number.EPSILON) * factor) / factor;
  };

  const montarImpostosPerda = (impostos = []) =>
    impostos?.map((documentoItemImposto) => {
      const imposto = { ...documentoItemImposto };

      delete imposto?.id;
      delete imposto?.documento_item_id;
      delete imposto?.Tributo;
      delete imposto?.SituacaoTributarium;

      return imposto;
    });

  const limitarQuantidade = (item, quantidade) => {
    const value = normalizarDecimal(quantidade);

    if (isNaN(value) || value <= 0) {
      return 0;
    }

    if (!item?.is_remessa || item?.is_perda) {
      return value;
    }

    const saldo_retornar = Number(item?.saldo_retornar || 0);

    return normalizarDecimal(Math.min(value, Math.max(saldo_retornar, 0)));
  };

  useEffect(() => {
    if (Boolean(id)) {
      getDocumento(id);
    }
  }, []);

  const onCellEditCommit = (e) => {
    setItens((prev) =>
      prev?.map((c) => {
        if (c?.id === e?.id) {
          if (e?.field === 'quantidade') {
            return { ...c, [e?.field]: limitarQuantidade(c, e?.value) };
          }
          return { ...c, [e?.field]: e?.value };
        } else {
          return { ...c };
        }
      })
    );
  };

  const onLoad = async (values) => {
    try {
      setLoading(true);

      const quantidade = normalizarDecimal(values?.quantidade);

      if (!values?.produto_id) {
        return toastWarning('Selecione o produto');
      }

      if (isNaN(quantidade) || quantidade <= 0) {
        return toastWarning('Quantidade inválida');
      }

      const produto = await checkInfosProduto({
        produto_id: values?.produto_id,
      });

      const montarItem = ({
        produto_id,
        descricao,
        referencia,
        preco,
        precobase_id,
        qtd,
        is_perda = false,
      }) => {
        const comp = itens_remessa?.find((f) => f?.produto_id === produto_id);
        const is_remessa = Boolean(comp);

        const gerados = comp?.Vinculos?.filter((f) =>
          [21, 118]?.includes(f?.natureza_operacao_id)
        );
        const quantidade_gerada = normalizarDecimal(
          summarizer(gerados, 'quantidade')
        );
        const quantidade_remessa = is_remessa
          ? normalizarDecimal(comp?.quantidade || 0)
          : 0;
        let quantidade_maxima = undefined;
        const saldo_retornar = is_remessa
          ? normalizarDecimal(quantidade_remessa - quantidade_gerada)
          : 0;

        if (comp) {
          quantidade_maxima = saldo_retornar;
        }

        const item = {
          id: `${produto_id}-${is_perda ? 'perda' : 'retorno'}`,
          produto_id,
          descricao: is_perda ? `[PERDA] ${descricao}` : descricao,
          quantidade_maxima,
          quantidade_remessa,
          saldo_retornar,
          referencia,
          preco: comp?.preco || preco || null,
          precobase_id,
          is_perda,
          is_remessa,
          id_remessa: is_remessa ? comp?.id : undefined,
        };

        return { ...item, quantidade: limitarQuantidade(item, qtd) };
      };

      const itensParaIncluir = [];
      const itensPerdaParaIncluir = [];

      itensParaIncluir.push(
        montarItem({
          produto_id: produto?.id,
          descricao: produto?.descricao,
          referencia: produto?.referencia,
          preco: produto?.preco,
          precobase_id: produto?.precobase_id,
          qtd: quantidade,
        })
      );

      produto?.Estruturas?.forEach((m) => {
        const qtd = normalizarDecimal(Number(m?.quantidade || 0) * quantidade);
        const perda = normalizarDecimal((Number(m?.perda || 0) / 100) * qtd);

        itensParaIncluir.push(
          montarItem({
            produto_id: m?.componente_id,
            descricao: m?.desc_comp || m?.desc_red,
            referencia: m?.referencia,
            preco: m?.preco,
            precobase_id: m?.precobase_id,
            qtd,
          })
        );

        if (perda > 0) {
          itensPerdaParaIncluir.push(
            montarItem({
              produto_id: m?.componente_id,
              descricao: m?.desc_comp || m?.desc_red,
              referencia: m?.referencia,
              preco: m?.preco,
              precobase_id: m?.precobase_id,
              qtd: perda,
              is_perda: true,
            })
          );
        }
      });

      itensParaIncluir.push(...itensPerdaParaIncluir);

      const temAlgumItemNaRemessa = itensParaIncluir.some(
        (item) => item?.is_remessa
      );

      if (!temAlgumItemNaRemessa) {
        return toastWarning('Nenhum item da estrutura contém na remessa');
      }

      setItens((prev) => {
        const newArr = prev.map((item) => ({ ...item }));

        itensParaIncluir.forEach((item) => {
          const index = newArr.findIndex((s) => s?.id === item?.id);

          if (index >= 0) {
            const quantidade = normalizarDecimal(
              Number(newArr[index]?.quantidade || 0) +
                Number(item?.quantidade || 0)
            );

            newArr[index] = {
              ...newArr[index],
              quantidade: limitarQuantidade(newArr[index], quantidade),
            };
          } else {
            newArr.push({
              ...item,
              quantidade: limitarQuantidade(item, item?.quantidade),
            });
          }
        });

        return newArr;
      });

      reset();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = () => {
    const especie_id = documento?.Especie?.fiscal === 'SIM' ? 10 : 25;
    const natureza_operacao_id =
      documento?.natureza_operacao_id === 119 ? 118 : 21;
    const _itens = itens?.filter((f) => f?.quantidade && f?.quantidade > 0);
    const valor_itens = _itens
      ?.filter((f) => !f?.is_remessa && !f?.is_perda)
      ?.map((m) => ({
        ...m,
        preco_total: normalizarDecimal(
          Number(m?.quantidade || 0) * Number(m?.preco || 0)
        ),
      }));
    const valor = summarizer(valor_itens, 'preco_total');
    const dt = moment().format('YYYY-MM-DD HH:mm:ss');

    const item_errado = _itens?.find(
      (f) => f?.is_remessa && f?.quantidade > f?.saldo_retornar
    );
    if (Boolean(item_errado)) {
      return toastWarning(
        `${item_errado?.descricao} excede o saldo a retornar de ${item_errado?.saldo_retornar}`
      );
    }

    let data = {
      Documento: {
        especie_id,
        natureza_operacao_id,
        users_id: user?.id,
        filial_id: user?.filial_id,
        cadastro_id: documento?.cadastro_id,
        dtemissao: dt,
      },
      Itens: _itens?.map((i, index) => {
        const item = {
          sequencia: index + 1,
          descricao: i?.descricao,
          quantidade: normalizarDecimal(i?.quantidade || 0),
          preco: i?.preco,
        };

        return {
          ...item,
          produto_id: i?.produto_id,
          referencia: i?.referencia,
          precobase_id: i?.precobase_id,
          ...(Boolean(i?.id_remessa) && {
            Origens: [{ documento_item_org_id: i?.id_remessa }],
            Vinculos: [{ documento_item_id_vinculo: i?.id_remessa }],
          }),
        };
      }),
      Origens: [{ documento_id: id }],
      ...(valor > 0 && {
        Financeiros: [
          { forma_pagto_id: 1, parcela: 1, dtvenc: dt, valor, fixo: true },
        ],
      }),
    };

    if (documento?.Especie?.Grupo === 'SAIDA') {
      data = { ...data, Saida: { vendedor_id: user?.entidade_id } };
    }

    if (documento?.Especie?.Grupo === 'ENTRADA') {
      data = { ...data, Entrada: { dtentrada: dt } };
    }

    if (data?.Itens?.length <= 0) {
      return toastWarning('Não há itens para retornar');
    }

    postDocumento({ data, cb: () => navigate(-1) });
  };

  return (
    <Container>
      <Header titulo="Retorno de Remessa" />
      <Card>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <VirtualDrop
              label="Produto"
              name="produto_id"
              control={control}
              options={drops?.Produto}
            />
          </Grid>
          <Grid item xs={4} display="flex" gap={2}>
            <Box flex={1}>
              <InputMask
                control={control}
                name="quantidade"
                label="Quantidade"
              />
            </Box>
            <Button
              loading={isLoading}
              variant="outlined"
              onClick={handleSubmit(onLoad)}
            >
              Carregar
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ height: '70vh' }}>
              <DataGrid
                rows={itens}
                columns={[
                  {
                    field: 'referencia',
                    headerName: 'Referência',
                    width: 200,
                    sortable: false,
                  },
                  {
                    field: 'descricao',
                    headerName: 'Produto',
                    flex: 1,
                    minWidth: 400,
                    sortable: false,
                  },
                  {
                    field: 'quantidade_remessa',
                    headerName: 'Quantidade na Remessa',
                    type: 'number',
                    width: 200,
                    sortable: false,
                  },
                  {
                    field: 'saldo_retornar',
                    headerName: 'Saldo a Retornar',
                    type: 'number',
                    width: 200,
                    sortable: false,
                  },
                  {
                    field: 'quantidade',
                    headerName: 'Quantidade',
                    type: 'number',
                    width: 200,
                    editable: true,
                    sortable: false,
                  },
                  {
                    field: 'preco',
                    headerName: 'Preço Unitário',
                    type: 'number',
                    width: 200,
                    editable: true,
                    sortable: false,
                  },
                  {
                    field: 'is_remessa',
                    headerName: 'Remessa',
                    type: 'boolean',
                    width: 100,
                    sortable: false,
                  },
                  {
                    field: 'actions',
                    headerName: 'Ações',
                    type: 'actions',
                    width: 100,
                    getActions: (params) => [
                      <GridActionsCellItem
                        icon={<Icon>delete</Icon>}
                        label="Excluir"
                        onClick={() =>
                          setItens(itens?.filter((r) => r?.id !== params?.id))
                        }
                      />,
                    ],
                  },
                ]}
                autoPageSize
                density="compact"
                disableSelectionOnClick
                disableColumnMenu
                showCellRightBorder
                showColumnRightBorder
                getRowId={(row) => row?.id}
                onCellEditCommit={onCellEditCommit}
                localeText={localeText}
                loading={isLoading}
                components={{
                  LoadingOverlay: LinearProgress,
                  Pagination: CustomPagination,
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} textAlign="center">
            <Button
              variant="contained"
              color="primary"
              loading={isLoading}
              onClick={onSubmit}
            >
              FINALIZAR
            </Button>
          </Grid>
        </Grid>
      </Card>
    </Container>
  );
};

export default Retorno;
